import { isArray, xor, isEmpty, set } from "lodash";
import memoizeOne from "memoize-one";
import moment from "moment";
import type { Data, Layout, Config as PlotlyConfig } from "plotly.js";
import { lazy, Suspense } from "react";
import type React from "react";
import { connect } from "react-redux";
import type { VisualizationSpec } from "react-vega";
import styled from "styled-components";
import tinycolor from "tinycolor2";
import type { View } from "vega";
import { hash, type Config } from "vega-lite";
import type { StringFieldDef } from "vega-lite/types_unstable/channeldef.js";
import type { MarkDef } from "vega-lite/types_unstable/mark.js";
import type { Transform } from "vega-lite/types_unstable/transform.js";

import equal from "@superblocksteam/fast-deep-equal/es6";
import type {
  PerCornerBorderRadius,
  PerSideBorder,
} from "@superblocksteam/shared";

import { updateWidgetProperties } from "legacy/actions/controlActions";
import type { UpdateWidgetPropertiesPayload } from "legacy/actions/controlActions";
import type { WidgetAddChild } from "legacy/actions/pageActions";
import EmptyDataState from "legacy/components/utils/EmptyDataState";
import Skeleton from "legacy/components/utils/Skeleton";
import { EventType } from "legacy/constants/ActionConstants";
import type { MultiStepDef } from "legacy/constants/ActionConstants";
import type { PropertyPaneConfig } from "legacy/constants/PropertyControlConstants";
import { ReduxActionTypes } from "legacy/constants/ReduxActionConstants";
import type { ReduxAction } from "legacy/constants/ReduxActionConstants";
import { WidgetTypes } from "legacy/constants/WidgetConstants";
import type { WidgetType } from "legacy/constants/WidgetConstants";
import { VALIDATION_TYPES } from "legacy/constants/WidgetValidation";
import type { WidgetPropertyValidationType } from "legacy/constants/WidgetValidation";
import type {
  DataTree,
  DataTreeWidget,
} from "legacy/entities/DataTree/dataTreeFactory";
import { WidgetIcons } from "legacy/icons/WidgetIcons";
import { APP_MODE } from "legacy/reducers/types";
import { getResponsiveCanvasScaleFactor } from "legacy/selectors/applicationSelectors";
import { selectGeneratedTheme } from "legacy/selectors/themeSelectors";
import type { GeneratedTheme, TextStyleWithVariant } from "legacy/themes";
import { CLASS_NAMES } from "legacy/themes/classnames";
import { retryPromise } from "legacy/utils/Utils";
import type { AppState } from "store/types";
import { fastClone } from "utils/clone";
import { getComponentDimensions } from "utils/size";

import { generateBorderStyleObject } from "../base/generateBorderStyle";
import BaseWidget from "../BaseWidget";
import type { WidgetState, WidgetPropsRuntime } from "../BaseWidget";
import type {
  WidgetActionHook,
  CanvasWidgetsReduxState,
  WidgetActionResponse,
} from "../Factory";
import withMeta from "../withMeta";
import type { WithMeta } from "../withMeta";
import { ChartHeader } from "./ChartHeader";
import ChartWidgetPropertyCategories from "./ChartWidgetPropertyCategories";
import type { TimeUnit } from "./types";
import {
  getPrimaryDimensionMeta,
  getTimeScale,
  isNonXYChart,
  isTemporalXYChart,
} from "./utils";
import { isXYChart } from "./utils";

// just for testing

const HEADER_HEIGHT = 58;

// Hides the Vega actions until hover
const ChartWrapper = styled.div`
  position: relative;
  overflow: hidden;

  .vega-embed summary {
    display: none;
  }

  .vega-embed:hover summary {
    display: list-item;
  }
`;

const Scaler = styled.div<{ scaleFactor: number }>`
  transform: scale(${(props) => props.scaleFactor});
  transform-origin: top left;
`;

const EmptyWrapper = styled(ChartWrapper)`
  display: flex;
  flex-direction: column;

  > div {
    flex-basis: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // center text with widget icon
  span {
    position: relative;
    top: -2px;
  }

  svg > * {
    fill: ${({ theme }) => theme.palettes.gray[7]};

    :not([opacity]) {
      fill: ${({ theme }) => theme.palettes.gray[4]};
    }
  }
`;

const PlotWrapper = styled.div`
  :hover {
    cursor: auto;
  }
`;

const EmptyChart = (props: ChartWidgetProps) => (
  <EmptyWrapper className={CLASS_NAMES.DEFAULT_CONTAINER}>
    <ChartHeader title={props.headerTitle} headerProps={props.headerProps} />
    <div>
      <WidgetIcons.CHART_WIDGET />
      <span>No data</span>
    </div>
  </EmptyWrapper>
);

const LazyVega = lazy(() =>
  retryPromise(() => import(/* webpackChunkName: "vega" */ "./VegaDefault")),
);

const vegaActions = {
  source: false,
  compiled: false,
};

const LazyPlot = lazy(() =>
  retryPromise(
    () => import(/* webpackChunkName: "plotly" */ "./PlotlyDefault"),
  ),
);

const sanitizeFieldName = (field: string): string =>
  field.replace(/\.|\[|\]/g, "_");

function getEncodingFields(props: ChartWidgetProps): string[] {
  return [
    props.groupBy.field,
    props.x.field,
    ...props.y.series.map((s) => s.field),
    props.groupBy.field && props.y.series.length
      ? "superblocks_calculated_key"
      : "",
    props.y.series.length > 1 ? "superblocks_key" : "",
    props.y.series.length > 1 ? "superblocks_value" : "",
  ].filter((key) => Boolean(key));
}

// 1. columnInfo is always kept up to date with the chartData, and controls
//    what users see in the dropdown options for their chart
//
// 2. If the user has modified the chartData bindings we need to re-initialize
function setColumnInfo(props: ChartWidgetProps) {
  if (isEmpty(props.chartData)) {
    return;
  }

  let newColumns: string[] = [];
  let newColumnInfo: ChartWidgetProps["columnInfo"] = [];
  const chartData = props.chartData ?? [];
  if (!isArray(chartData)) {
    newColumns = [];
  } else {
    newColumns = (Object.keys(chartData?.[0] ?? {}) ?? []).sort();
    if (chartData && chartData[0]) {
      newColumnInfo = newColumns.map((key) => {
        const value = chartData[0][key];
        const base = { name: sanitizeFieldName(key) };
        const defaultType = typeof value;
        if (defaultType === "number" || defaultType === "boolean") {
          return { ...base, type: defaultType, canBeNumeric: true };
        }
        if (defaultType === "string") {
          if (moment(value as string, moment.ISO_8601).isValid()) {
            return { ...base, type: "date", canBeNumeric: false };
          }
          const canBeNumeric = Number.isFinite(parseFloat(value as string));
          return { ...base, type: defaultType, canBeNumeric };
        }
        return base;
      });
    }
  }
  const currentColumnNames = (props.columnInfo ?? []).map((c) =>
    sanitizeFieldName(c.name),
  );

  if (props.shouldReinitialize) {
    // Set initial column assignments
    const numericFields = newColumnInfo.filter(({ type }) => type === "number");
    const dateFields = newColumnInfo.filter(({ type }) => type === "date");
    const stringFields = newColumnInfo.filter(({ type }) => type === "string");
    const batchedChanges: Record<string, unknown> = {};

    if (dateFields.length) {
      batchedChanges["x.field"] = dateFields[0].name;
      if (numericFields.length) {
        numericFields.forEach((info, index) => {
          batchedChanges[`y.series[${index}]`] = {
            field: info.name,
          };
        });
      }
    } else if (stringFields.length) {
      batchedChanges["x.field"] = stringFields[0].name;
      if (numericFields.length) {
        numericFields.forEach((info, index) => {
          batchedChanges[`y.series[${index}]`] = {
            field: info.name,
          };
        });
      }
    } else if (numericFields.length) {
      if (numericFields[0]?.name) {
        batchedChanges["x.field"] = numericFields[0].name;
      }
      numericFields.slice(1).forEach((info, index) => {
        batchedChanges[`y.series[${index}]`] = {
          field: info.name,
        };
      });
    }

    // If the groupBy field is no longer in the data, remove it
    if (props.groupBy.field && !newColumns.includes(props.groupBy.field)) {
      batchedChanges["groupBy.field"] = undefined;
    }
    return { columnInfo: newColumnInfo, ...batchedChanges };
  } else {
    const changedColumns = xor(newColumns, currentColumnNames);
    if (changedColumns.length) {
      const batchedChanges: Record<string, unknown> = {
        columnInfo: newColumnInfo,
      };
      if (props.groupBy.field && !newColumns.includes(props.groupBy.field)) {
        batchedChanges["groupBy.field"] = undefined;
      }
      if (!newColumns.includes(props.x.field)) {
        batchedChanges["x.field"] = undefined;
      }

      return batchedChanges;
    }
  }
}

function getChartSizeInfo(props: ChartWidgetProps) {
  const { componentHeight, componentWidth } = getComponentDimensions(props);
  const headerHeightPx = !isEmpty(props.headerTitle) ? HEADER_HEIGHT : 0;

  const chartHeight = componentHeight - headerHeightPx;
  const chartWidth = componentWidth;

  return { headerHeight: headerHeightPx, chartHeight, chartWidth };
}

export default class ChartWidget extends BaseWidget<
  ChartWidgetProps,
  WidgetState
> {
  calculateWrapperStyle(
    border: PerSideBorder,
    borderRadius: PerCornerBorderRadius,
    background: string,
  ): React.CSSProperties {
    const borderStyle = generateBorderStyleObject({
      border,
      borderRadius,
    });
    return {
      background,
      ...borderStyle,
    };
  }

  memoizedCalculateWrapperStyle = memoizeOne(this.calculateWrapperStyle);

  static getPropertyValidationMap(): WidgetPropertyValidationType {
    return {
      headerTitle: VALIDATION_TYPES.TEXT,
      chartData: VALIDATION_TYPES.TABLE_DATA,
      chartType: VALIDATION_TYPES.TEXT,
      plotlyChartJson: VALIDATION_TYPES.PLOTLY_CHART_JSON,
      plotlyChartLayout: VALIDATION_TYPES.OBJECT_OR_UNDEFINED,
      plotlyChartConfig: VALIDATION_TYPES.OBJECT_OR_UNDEFINED,
      "x.field": VALIDATION_TYPES.TEXT,
      border: VALIDATION_TYPES.OBJECT_OR_UNDEFINED,
      borderRadius: VALIDATION_TYPES.OBJECT_OR_UNDEFINED,
      // TODO: display nested props correctly in evaludated pop up
      // "y.minimumFractionDigits": VALIDATION_TYPES.FRACTION_DIGITS,
      // "y.maximumFractionDigits": VALIDATION_TYPES.FRACTION_DIGITS,
    };
  }

  static getNewPropertyPaneConfig():
    | PropertyPaneConfig<ChartWidgetProps>[]
    | undefined {
    return ChartWidgetPropertyCategories;
  }

  static getPropertyPaneConfig(): PropertyPaneConfig[] {
    throw new Error("Deprecated config should not be called");
  }

  static getMetaPropertiesMap(): Record<string, any> {
    return {
      selectedData: [],
    };
  }

  static applyActionHook: WidgetActionHook = function (params: {
    widgetId: string;
    widgets: Readonly<CanvasWidgetsReduxState>;
    action: ReduxAction<
      DataTree | UpdateWidgetPropertiesPayload | WidgetAddChild
    >;
    appMode: APP_MODE;
    evaluatedWidgets: Record<string, DataTreeWidget>;
  }): WidgetActionResponse {
    const { widgetId, widgets, action, appMode, evaluatedWidgets } = params;

    if (widgets[widgetId].type !== WidgetTypes.CHART_WIDGET) {
      return {
        widgetUpdates: [],
        widgetMetaUpdates: [],
      };
    }

    const widget = widgets[widgetId] as unknown as Readonly<ChartWidgetProps>;

    let widgetUpdates:
      | WidgetActionResponse["widgetUpdates"][number]["widget"]
      | undefined = undefined;

    let widgetMetaUpdates:
      | WidgetActionResponse["widgetMetaUpdates"][number]["updates"]
      | undefined = undefined;

    switch (action.type) {
      case ReduxActionTypes.WIDGET_CREATE: {
        // Do not handle widget create for anything but the current widget
        if ((action.payload as WidgetAddChild).newWidgetId !== widgetId) {
          return {
            widgetUpdates: [],
            widgetMetaUpdates: [],
          };
        }
        if (
          (action.payload as WidgetAddChild).type !== WidgetTypes.CHART_WIDGET
        ) {
          // Don't re-initialize for non-charts
          break;
        }
        // Wait for the initial data to be evaluated
        widgetMetaUpdates = { shouldReinitialize: true };
        break;
      }
      case updateWidgetProperties.type: {
        // Don't update a different chart
        if (
          !updateWidgetProperties.match(action) ||
          action.payload.widgetId !== widgetId
        ) {
          return {
            widgetUpdates: [],
            widgetMetaUpdates: [],
          };
        }
        if (!widget.shouldReinitialize && action.payload.updates.chartData) {
          // Set this flag when the user updates the chartData property
          widgetMetaUpdates = { shouldReinitialize: true };
        }
        break;
      }
      case ReduxActionTypes.TREE_WILL_UPDATE: {
        if (appMode !== APP_MODE.EDIT) {
          break;
        }

        const previouslyEvaluatedWidget: ChartWidgetProps | undefined =
          evaluatedWidgets[widgetId] as unknown as ChartWidgetProps | undefined;

        // In the case of a rename that happens during an eval, the name will be the new name of the widget and that name will not be in the data tree
        // the rename will cause an evaluation of the widget which will then trigger this on the second evaluation
        const name = widgets[widgetId].widgetName;
        const evaluatedWidget = (action.payload as DataTree).PAGE[
          name
        ] as unknown as ChartWidgetProps;

        if (!evaluatedWidget) {
          break;
        }

        const chartDataModified = !equal(
          evaluatedWidget.chartData,
          previouslyEvaluatedWidget?.chartData,
        );
        if (chartDataModified) {
          const updates = setColumnInfo(evaluatedWidget);

          if (updates) {
            const updatedWidget = fastClone(widget);
            Object.entries(updates).forEach(([path, update]) => {
              set(updatedWidget, path, update);
            });
            widgetMetaUpdates = { shouldReinitialize: false };
            widgetUpdates = updatedWidget;
          }
        }

        break;
      }
    }

    return {
      widgetUpdates: widgetUpdates
        ? [
            {
              widgetId,
              widget: widgetUpdates,
            },
          ]
        : [],
      widgetMetaUpdates: widgetMetaUpdates
        ? [
            {
              widgetId,
              updates: widgetMetaUpdates,
            },
          ]
        : [],
    };
  };

  private vegaView: View | undefined;

  stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  preventDefault = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  getPlotlyView() {
    if (
      !this.props.isLoading &&
      (isEmpty(this.props.plotlyChartJson) ||
        isEmpty(this.props.plotlyChartJson.data))
    ) {
      return <EmptyChart {...this.props} />;
    }

    const { chartHeight, chartWidth } = getChartSizeInfo(this.props);
    let mutableLayout = this.props.plotlyChartJson?.layout
      ? fastClone(this.props.plotlyChartJson.layout ?? {})
      : {};
    if (this.props.plotlyChartJson?.layout) {
      mutableLayout.height = chartHeight;
      mutableLayout.width = chartWidth;
      if (!isEmpty(this.props.plotlyChartLayout)) {
        mutableLayout = Object.assign(
          {},
          mutableLayout,
          fastClone(this.props.plotlyChartLayout),
        );
      }
    }

    const mutableData = fastClone(this.props.plotlyChartJson?.data);

    const mutableConfig = {
      ...fastClone(this.props.plotlyChartJson?.config),
      ...fastClone(this.props.plotlyChartConfig),
    };

    // we don't support Plotly's editable mode
    if ("editable" in mutableConfig) {
      mutableConfig.editable = false;
    }

    const wrapperStyle = this.memoizedCalculateWrapperStyle(
      this.props.border,
      this.props.borderRadius,
      this.props.backgroundColor,
    );

    const plotlyKey = memoizeOne(() =>
      hash({ mutableData, mutableLayout, mutableConfig }),
    );

    return (
      <ChartWrapper
        className={CLASS_NAMES.DEFAULT_CONTAINER}
        style={wrapperStyle}
      >
        <ChartHeader
          title={this.props.headerTitle}
          headerProps={this.props.headerProps}
        />
        <Suspense fallback={<Skeleton />}>
          <PlotWrapper
            className={this.props.isLoading ? "bp5-skeleton" : ""}
            onMouseDown={this.stopPropagation}
            onMouseOver={this.preventDefault}
          >
            <LazyPlot
              // force rerender Plotly charts when any of the data changes
              key={plotlyKey()}
              data={mutableData}
              layout={mutableLayout}
              config={mutableConfig}
            />
          </PlotWrapper>
        </Suspense>
      </ChartWrapper>
    );
  }

  getPageView() {
    if (!this.props.chartType) {
      return <EmptyDataState />;
    }

    if (this.props.chartDefinition === "plotly") {
      return this.getPlotlyView();
    }

    if (!this.props.isLoading && isEmpty(this.props.chartData)) {
      return <EmptyChart {...this.props} />;
    }

    if (
      isXYChart(this.props) &&
      !this.props.x.field &&
      !this.props.y.series?.length
    ) {
      return <EmptyDataState />;
    }
    if (isNonXYChart(this.props) && !this.props.size.field) {
      return <EmptyDataState />;
    }

    const { chartHeight, chartWidth } = getChartSizeInfo(this.props);
    // Vega's user interaction doesn't play well with canvas scaling, so we
    // scale the vega chart component by the inverser of the canvas scale factor
    // with CSS and then let Vega handle the scaling by passing in a scaled
    // width and height.
    const scaleFactor = this.props.canvasScaleFactor;

    const wrapperStyle = this.memoizedCalculateWrapperStyle(
      this.props.border,
      this.props.borderRadius,
      this.props.backgroundColor,
    );

    return (
      <ChartWrapper
        className={CLASS_NAMES.DEFAULT_CONTAINER}
        style={wrapperStyle}
      >
        <ChartHeader
          title={this.props.headerTitle}
          headerProps={this.props.headerProps}
        />

        <Suspense fallback={<Skeleton />}>
          <Scaler scaleFactor={1 / scaleFactor}>
            <LazyVega
              className={this.props.isLoading ? "bp5-skeleton" : ""}
              renderer="canvas"
              height={chartHeight * scaleFactor}
              width={chartWidth * scaleFactor}
              spec={this.getSpec()}
              onNewView={(view) => {
                if (this.vegaView) {
                  this.vegaView.removeSignalListener(
                    "select",
                    this.onSelectData,
                  );
                }
                this.vegaView = view;
                view.addSignalListener("select", this.onSelectData.bind(this));
              }}
              actions={vegaActions}
            />
          </Scaler>
        </Suspense>
      </ChartWrapper>
    );
  }

  getMark(): MarkDef {
    switch (this.props.chartType) {
      case "AREA_CHART":
        return {
          type: "area",
          line: {
            color: this.props.generatedTheme.colors.primary500,
            strokeWidth: 1.5,
          },
        };
      case "LINE_CHART":
        return {
          type: "line",
          strokeWidth: 1.5,
          point: { size: 40 },
        };
      case "BAR_CHART":
        return {
          type: "bar",
          cornerRadiusBottomRight: 4,
          cornerRadiusTopRight: 4,
        };
      case "COLUMN_CHART":
        return {
          type: "bar",
          cornerRadiusTopLeft: 4,
          cornerRadiusTopRight: 4,
        };
      case "PIE_CHART":
        return { type: "arc" };
      case "SCATTER_CHART":
        return { type: "point" };
    }
    return { type: "point", size: 40, filled: false };
  }

  getConfig(): Config {
    const hasColorChannel =
      this.props.y.series.length > 1 || this.props.groupBy.field;

    const horizontalPositionedLegend =
      this.props.legend?.position === "top" ||
      this.props.legend?.position === "bottom";

    return {
      customFormatTypes: true,
      font: `${this.props.generatedTheme.fontFamily}, sans-serif`,
      padding: 24,
      background: "transparent",
      axis: {
        tickWidth: 0.5,
        labelColor: this.props.generatedTheme.colors.neutral500,
        domainColor: this.props.generatedTheme.colors.neutral100,
        labelOverlap: true,
        labelFontSize: 12,
        labelPadding: 5,
        titleFontSize: 14,
        titlePadding: 10,
        gridColor: this.props.generatedTheme.colors.neutral200,
        gridDash: [4, 4],
      },
      axisY: {
        domain: false,
        gridWidth: 0.5,
        tickSize: 0,
        labelPadding: 5,
        grid: true,
      },
      legend: {
        symbolType: "circle",
        labelColor: this.props.generatedTheme.colors.neutral500,
        labelFontSize: 12,
        orient: this.props.legend?.position || "right",
        ...(horizontalPositionedLegend
          ? {
              layout: {
                bottom: { anchor: "middle" },
                top: { anchor: "middle" },
              } as any, // layout type is not defined in vega-lite but the configuration is valid
            }
          : {
              rowPadding: 12,
              labelOffset: 12,
              symbolSize: 80,
            }),
      },
      view: { stroke: "transparent" },
      arc: {
        strokeWidth: 0,
      },
      line: {
        color: this.props.generatedTheme.colors.primary500,
      },
      point: {
        size: 40,
        filled: true,
        fill: this.props.generatedTheme.colors.neutral,
        stroke: this.props.generatedTheme.colors.primary500,
        strokeWidth: hasColorChannel ? 0 : 2,
      },
      area: {
        color: {
          x1: 1,
          y1: 1,
          x2: 1,
          y2: 0,
          gradient: "linear",
          stops: [
            {
              offset: 0,
              color: tinycolor(this.props.generatedTheme.colors.primary500)
                .setAlpha(0.04)
                .toRgbString(),
            },
            {
              offset: 1,
              color: tinycolor(this.props.generatedTheme.colors.primary500)
                .setAlpha(0.32)
                .toRgbString(),
            },
          ],
        },
      },
      bar: {
        fill: this.props.generatedTheme.colors.primary500,
      },
    };
  }

  // This function localizes the timezone of the data to the browser's timezone.
  localizeTimezone = (data: Record<string, any>[]): Record<string, any>[] => {
    try {
      return data.map((datum) => {
        // If the datum has a time field, localize it to the browser's timezone.
        if (datum[this.props.x.field]) {
          const date = new Date(datum[this.props.x.field]);
          const localizedDate = new Date(
            date.getTime() + date.getTimezoneOffset() * 60000,
          );
          datum[this.props.x.field] = localizedDate.toISOString();
        }
        return datum;
      });
    } catch (e) {
      // If there is an error, just return the data as is.
      console.error("Error localizing timezone: ", e);
      return data;
    }
  };

  getSpec(): VisualizationSpec {
    const data = fastClone(this.props.chartData);
    const showLegend =
      !this.props.legend || this.props.legend.position !== "none";
    // TODO: look into why sometimes `this.props.chartData` comes in string format
    const sanitizedData = isArray(data)
      ? data.map((el) => {
          const sanitized: Record<string, any> = {};
          Object.entries(el).forEach(([key, val]) => {
            if (typeof key === "string")
              sanitized[sanitizeFieldName(key)] = val;
          });
          return sanitized;
        })
      : data;

    let localizedSanitizedData = sanitizedData;
    if (isTemporalXYChart(this.props)) {
      localizedSanitizedData = this.localizeTimezone(sanitizedData);
    }
    const { chartType, x, y } = this.props;
    const usedFields = getEncodingFields(this.props);
    const usedFieldsSanitized = usedFields.map((field) =>
      sanitizeFieldName(field),
    );
    const hasMultipleSeries = y.series.length > 1;

    const baseSpec: VisualizationSpec = {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      autosize: {
        type: "fit",
        contains: "padding",
      },
      mark: this.getMark(),
      data: { values: localizedSanitizedData },
      params: [
        {
          name: "select",
          select: { type: "point", fields: usedFieldsSanitized },
        },
      ],
      encoding: {
        ...(this.props.groupBy.field
          ? {
              color: {
                field: this.props.groupBy.field,
                type: "nominal",
                legend: showLegend ? { title: null } : null,
              },
            }
          : {}),
        opacity: {
          condition: [{ param: "select", value: 1 }],
          value: 0.2,
        },
      },
      config: this.getConfig(),
    };

    if (isNonXYChart(this.props)) {
      return {
        ...baseSpec,
        mark: { ...(baseSpec.mark as MarkDef), tooltip: true },
        encoding: {
          ...baseSpec.encoding,
          order: {
            field: this.props.size.field,
            type: "quantitative",
            sort: "descending",
          },
          theta: {
            field: this.props.size.field,
            type: "quantitative",
            aggregate: "sum",
          },
        },
      };
    } else {
      const primaryDimension = {
        ...getPrimaryDimensionMeta(this.props),
        field: x.field,
      };

      const secondaryDimension = {
        ...(y.aggregation ? { aggregate: y.aggregation } : {}),
        type: "quantitative",
        field: hasMultipleSeries ? "superblocks_value" : y.series[0]?.field,
        title: hasMultipleSeries ? "value" : y.series[0]?.field,
      } as const;

      const hasColorChannel = hasMultipleSeries || this.props.groupBy.field;
      const showLegend =
        !this.props.legend || this.props.legend.position !== "none";
      const groupBy = hasColorChannel
        ? ({
            field:
              hasMultipleSeries && this.props.groupBy.field
                ? "superblocks_calculated_key"
                : hasMultipleSeries
                  ? "superblocks_key"
                  : this.props.groupBy.field,
            type: "nominal",
            title: this.props.groupBy.field ? "Group by" : "Series",
          } as const)
        : {};

      let tooltip: any = [primaryDimension, secondaryDimension, groupBy].filter(
        (dimension) => !isEmpty(dimension),
      );

      if (primaryDimension.timeUnit === "yearweek") {
        tooltip = tooltip.filter((t: any) => t.timeUnit !== "yearweek");
        const tooltipReplacement: StringFieldDef<string> = {
          format: "%b %d, %Y",
          type: "temporal",
          title: `${primaryDimension.field} (year-month-date)`, // same title format as yearmonthdate
        };
        if (secondaryDimension.aggregate) {
          tooltipReplacement.field = `yearweek_${primaryDimension.field}`;
        } else {
          tooltipReplacement.field = "superblocks_year_month_date";
        }
        tooltip.push(tooltipReplacement);
      }

      const transform: Transform[] = hasMultipleSeries
        ? [
            {
              fold: y.series
                .map(({ field }) => field)
                .filter((y) => Boolean(y)),
              as: ["superblocks_key", "superblocks_value"],
            },
            ...(this.props.groupBy.field
              ? [
                  {
                    calculate: `toString(datum['${this.props.groupBy.field}']) + ' ' + toString(datum.superblocks_key)`,
                    as: "superblocks_calculated_key",
                  },
                ]
              : []),
          ]
        : [];

      if (
        primaryDimension.timeUnit === "yearweek" &&
        !secondaryDimension.aggregate
      ) {
        transform.push({
          timeUnit: "yearmonthdate",
          field: primaryDimension.field,
          as: "superblocks_year_month_date",
        });
      }

      return {
        ...baseSpec,
        transform,
        encoding: {
          ...baseSpec.encoding,
          [chartType === "BAR_CHART" ? "y" : "x"]: {
            ...primaryDimension,
            // Use the order from the user's data, not alphabetical order
            ...(primaryDimension.type === "nominal" ? { sort: null } : {}),
            axis: {
              title: x.axisTitle,
              ...(primaryDimension.type === "temporal" &&
              primaryDimension.timeUnit === "yearmonth"
                ? { tickCount: "month" }
                : {}),
              ...(primaryDimension.timeUnit === "yearweek"
                ? { format: "%b %d, %Y", tickCount: "week" } // same as yearmonthdate
                : {}),
              labelAngle: 0,
              grid: chartType !== "BAR_CHART" && chartType !== "COLUMN_CHART",
              gridDash: false,
              gridColor: this.props.generatedTheme.colors.neutral100,
              titleColor: this.props.generatedTheme.colors.neutral500,
            },
            ...(isTemporalXYChart(this.props)
              ? getTimeScale(this.props.x.timeUnit)
              : {}),
          },
          [chartType === "BAR_CHART" ? "x" : "y"]: {
            ...secondaryDimension,
            axis: {
              title: y.axisTitle,
              tickCount: 5,
              format: {
                dataType: this.props.y.dataType,
                notation: this.props.y.notation,
                minimumFractionDigits: this.props.y.minimumFractionDigits,
                maximumFractionDigits: this.props.y.maximumFractionDigits,
                currency: this.props.y.currency,
              },
              formatType: "dataFormat",
              titleColor: this.props.generatedTheme.colors.neutral500,
            },
          },
          ...(hasColorChannel
            ? {
                color: {
                  ...groupBy,
                  legend: showLegend ? { title: null } : null,
                },
              }
            : {}),
          tooltip,
        },
      };
    }
  }

  onSelectData(
    signal: string,
    row: { vlPoint: { or: Record<string, Record<string, unknown>> } } & Record<
      string,
      unknown[]
    >,
  ) {
    try {
      if (!Object.keys(row).length) {
        if (this.props.selectedData && this.props.selectedData.length > 0) {
          this.props.updateWidgetMetaProperty("selectedData", []);
        }
        return;
      }
      const possibleRows = Object.values(row.vlPoint.or);
      const outputRows = possibleRows.map((possible) => {
        const output: Record<string, unknown> = {};
        if (possible.superblocks_key) {
          output[possible.superblocks_key as string] =
            possible.superblocks_value;
        } else if (this.props.y.series.length === 1) {
          output[this.props.y.series[0].field] =
            possible[this.props.y.series[0].field];
        }
        if (this.props.x.field) {
          output[this.props.x.field] = possible[this.props.x.field];
        }
        if (this.props.groupBy.field) {
          output[this.props.groupBy.field] = possible[this.props.groupBy.field];
        }
        if (this.props.size.field) {
          output[this.props.size.field] = possible[this.props.size.field];
        }
        return output;
      });
      this.props.updateWidgetMetaProperty("selectedData", outputRows);
      if (this.props.onSelectData) {
        super.runEventHandlers({
          steps: this.props.onSelectData,
          type: EventType.ON_SELECT_DATA,
        });
      }
    } catch (e) {
      // Noop
      console.log(e);
    }
  }

  getWidgetType(): WidgetType {
    return "CHART_WIDGET";
  }
}

export interface ChartWidgetProps extends WidgetPropsRuntime, WithMeta {
  headerTitle?: string;
  chartData: Array<Record<string, unknown>>;
  chartType: string;
  isVisible?: boolean;
  x: {
    field: string;
    dataType: "auto" | "temporal" | "quantitative" | "ordinal" | "nominal";
    timeUnit?: TimeUnit;
    axisTitle: string;
  };
  y: {
    axisTitle: string;
    dataType: "number" | "currency";
    notation?: Intl.NumberFormatOptions["notation"];
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    currency?: string;
    aggregation?: "sum" | "count";
    series: Array<{
      field: string;
    }>;
  };
  groupBy: {
    field: string;
  };
  size: {
    field: string;
  };
  legend?: {
    position: "bottom" | "right" | "left" | "top" | "none";
  };
  // Flag set from the point that chartData property is modified until we get
  // the first complete data back from the server.
  shouldReinitialize?: boolean;
  columnInfo?: Array<{
    name: string;
    type?: "number" | "string" | "date" | "boolean";
    canBeNumeric?: boolean;
  }>;
  chartDefinition: string;
  plotlyChartJson: {
    data: Data[];
    layout?: Partial<Layout>;
    config?: Partial<PlotlyConfig>;
  };
  plotlyChartLayout?: Partial<Layout>;
  plotlyChartConfig?: Partial<PlotlyConfig>;
  canvasScaleFactor: number;
  selectedData: Array<Record<string, unknown>>; // TODO(MH): Verify this type
  onSelectData?: MultiStepDef;
  // style properties
  backgroundColor: string;
  border: PerSideBorder;
  borderRadius: PerCornerBorderRadius;
  headerProps?: {
    textStyle: TextStyleWithVariant;
  };
  // from state
  generatedTheme: GeneratedTheme;
}

const mapStateToProps = (state: AppState) => {
  return {
    canvasScaleFactor: getResponsiveCanvasScaleFactor(state),
    generatedTheme: selectGeneratedTheme(state),
  };
};

export const ConnectedChartWidget = connect(mapStateToProps)(
  withMeta(ChartWidget),
);
